import * as HoverCard from '@radix-ui/react-hover-card';
import React from 'react';

import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Flex } from '@/components/shared/layout/flex';
import { formatPrice } from '@/components/shared/utility/format';
import { CvModel } from '@/services/contentstack/models/cv-model';
import { keyframes, styled } from '@/stitches.config';
import { ITest } from '@/types/tracking';

const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(10px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-10px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-10px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(10px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const HoverCardTrigger = styled(HoverCard.Trigger, {});

const HoverCardContent = styled(HoverCard.Content, {
  width: '100%',
  maxWidth: 360,
  br: '$rounded-1-5',
  bc: '$white',
  bs: '$shadow-300',
  '@media (prefers-reduced-motion: no-preference)': {
    '--transition-duration': '0.4s, 1s',
    animationDuration: 'var(--transition-duration)',
    animationTimingFunction: 'var(--transition-easing)',
    willChange: 'transform, opacity',
    '&[data-state="open"]': {
      '&[data-side="top"]': { animationName: slideDownAndFade },
      '&[data-side="right"]': { animationName: slideLeftAndFade },
      '&[data-side="bottom"]': { animationName: slideUpAndFade },
      '&[data-side="left"]': { animationName: slideRightAndFade },
    },
  },
});

const ButtonInfo = styled('button', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  outline: 'none',
  border: 'none',
  bc: '$transparent',
  cursor: 'pointer',
});

const PriceWrap = styled('div', {
  padding: '$space-4',
});

interface IPriceHoverCard {
  models: CvModel[];
  test?: ITest;
}

const PriceHoverCard: React.FC<IPriceHoverCard> = ({
  models = [],
  test,
}: IPriceHoverCard) => {
  return (
    <>
      <HoverCard.Root openDelay={0} closeDelay={0}>
        <HoverCardTrigger asChild>
          <ButtonInfo
            aria-label="button"
            data-test={test?.dataTest || 'price_toolTips'}
          >
            <Icon icon="info" size="xs" color="icon-blue" />
          </ButtonInfo>
        </HoverCardTrigger>
        <HoverCardContent sideOffset={8} side="bottom" avoidCollisions={false}>
          <PriceWrap>
            {[...models]
              .sort((a, b) => a?.price - b?.price)
              .map((model: CvModel, index: number) => (
                <Flex
                  key={index}
                  alignItems="center"
                  justifyContent="center"
                  columnGap="2"
                >
                  <Text size="2xs" variant="text-gray">
                    รุ่น {model.name}:
                  </Text>
                  <Text font="bold" variant="text-gray-dark">
                    {formatPrice(model.price)}
                  </Text>
                </Flex>
              ))}
          </PriceWrap>
        </HoverCardContent>
      </HoverCard.Root>
    </>
  );
};

PriceHoverCard.displayName = 'SectionProductComparePriceHoverCard';
export default PriceHoverCard;
